import { useEffect, useRef, useState, type ChangeEvent } from 'react';
import { FileText, ImagePlus } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { useImageCrop } from '@/hooks/useImageCrop';
import { useUploadFile } from '@/hooks/useUploadFile';
import type { UploadKind } from '@/api/uploads.api';
import { isPdfUrl } from '@/lib/utils/media.utils';
import { getUploadErrorMessage } from '@/lib/utils/upload-error.utils';

const DEFAULT_ACCEPTED_TYPES = ['image/png', 'image/jpeg'];
const DEFAULT_PREVIEW_CLASS = 'h-24 w-40 shrink-0';

export interface CoverPhotoUploadFieldProps {
  /** Currently stored cover photo URL (edit mode), shown until replaced. */
  coverPhotoUrl?: string | null;
  /** Fired with the new S3 key once the presign-then-PUT flow finishes. */
  onUploaded: (fileKey: string) => void;
  /** Which upload purpose this cover photo belongs to (registry key, mirrors `AvatarUploadField.uploadKind`). */
  uploadKind: UploadKind;
  label: string;
  hint: string;
  uploadCaption: string;
  replaceCaption: string;
  wrongTypeMessage: string;
  tooLargeMessage: string;
  uploadFailedMessage: string;
  /** Shows the required-field asterisk next to the label, as `TextField` does. */
  required?: boolean;
  /** Caller-supplied validation error (e.g. a required-field message from the surrounding form). Shown only while there's no upload-specific error to report. */
  error?: string;
  /**
   * Fired whenever an upload starts or finishes, so the SURROUNDING FORM can
   * disable Save while one is in flight. Without it the form cannot tell an
   * upload is happening, and saving mid-transfer silently drops the image -
   * the key is not set yet, so the payload omits it and the save succeeds
   * against the OLD file with no error anywhere.
   */
  onUploadingChange?: (isUploading: boolean) => void;
  /**
   * MIME types this field accepts, e.g. `['image/png', 'image/jpeg', 'application/pdf']`.
   * Defaults to PNG/JPG (every existing caller). The Trusted Partner offer's
   * cover photo (data-fields.md: "File: PNG, JPG, PDF") is the one caller
   * that widens this - `upload-policies.ts` on the Backend already allows it
   * for that purpose.
   */
  acceptedTypes?: readonly string[];
  /**
   * Size classes for the preview box. Defaults to a small thumbnail; the Trusted
   * Partner profile passes the live size of the place the image is shown (FR-116),
   * so the member sees what a crop will look like in use.
   */
  previewClassName?: string;
  /**
   * Called with the file just picked (the cropped one, for an image) so a sibling, such as
   * the partner's live offer preview, can show it before the save. PDFs are reported too,
   * flagged, so the sibling can skip them.
   */
  onPreviewChange?: (preview: { url: string; isPdf: boolean }) => void;
  /** Puts the preview above the button instead of beside it; for a full-width preview. */
  stacked?: boolean;
}

/**
 * Rectangular cover-photo upload field: presigned-upload-then-confirm,
 * reusing the shared `useUploadFile` S3 flow. Promoted from
 * `features/admin/events/components/EventCoverPhotoField.tsx` to
 * `components/common/` once the Trusted Partner profile (FR-28) needed the
 * same rectangular-preview upload pattern for a second feature - the
 * mnr-standards promotion rule ("used by one feature -> keep in
 * features/<domain>/; a second feature needs it -> promote to common/").
 * `ui/Avatar`/`common/AvatarUploadField` are circular-only, so this stays its
 * own composite rather than a fork of either.
 *
 * Does NOT check image dimensions - each caller's own upload policy (server-
 * side) is the source of truth for accepted size/dimensions; this field only
 * enforces type and lets the server enforce the byte cap.
 */
export function CoverPhotoUploadField({
  coverPhotoUrl,
  onUploaded,
  uploadKind,
  label,
  hint,
  uploadCaption,
  replaceCaption,
  wrongTypeMessage,
  tooLargeMessage,
  uploadFailedMessage,
  error,
  required = false,
  onUploadingChange,
  acceptedTypes = DEFAULT_ACCEPTED_TYPES,
  previewClassName = DEFAULT_PREVIEW_CLASS,
  stacked = false,
  onPreviewChange,
}: CoverPhotoUploadFieldProps) {
  const upload = useUploadFile();
  const inputRef = useRef<HTMLInputElement>(null);
  const [localPreviewUrl, setLocalPreviewUrl] = useState<string | null>(null);
  const [localPreviewIsPdf, setLocalPreviewIsPdf] = useState(false);
  const [typeError, setTypeError] = useState<string | undefined>();
  const crop = useImageCrop(uploadKind, startUpload);

  useEffect(() => {
    return () => {
      if (localPreviewUrl) URL.revokeObjectURL(localPreviewUrl);
    };
  }, [localPreviewUrl]);

  useEffect(() => {
    onUploadingChange?.(upload.isPending);
  }, [upload.isPending, onUploadingChange]);

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;

    if (!acceptedTypes.includes(file.type)) {
      setTypeError(wrongTypeMessage);
      return;
    }

    setTypeError(undefined);
    crop.requestFile(file);
  };

  // The preview and upload start from the FINAL file: the cropped one for a PNG/JPEG,
  // the original for a PDF (FR-121). Cancelling the dialog changes nothing.
  function startUpload(file: File): void {
    const url = URL.createObjectURL(file);
    const isPdf = file.type === 'application/pdf';
    setLocalPreviewUrl(url);
    setLocalPreviewIsPdf(isPdf);
    onPreviewChange?.({ url, isPdf });
    upload.mutate(
      { kind: uploadKind, file },
      {
        onSuccess: (result) => onUploaded(result.fileKey),
        // The file never got stored, so drop its preview: keeping it showed the
        // unsaved image and "Replace" beside the failure message (MYN-271).
        onError: () => setLocalPreviewUrl(null),
      },
    );
  }

  const previewUrl = localPreviewUrl ?? coverPhotoUrl ?? null;
  const hasPreview = Boolean(previewUrl);
  // A stored key/URL carries no MIME type, so a PDF is inferred from its
  // extension for an existing (not just-uploaded) file.
  const isPdfPreview = localPreviewUrl ? localPreviewIsPdf : isPdfUrl(coverPhotoUrl);

  const errorMessage =
    typeError ??
    (upload.isError
      ? getUploadErrorMessage(upload.error, { tooLarge: tooLargeMessage, fallback: uploadFailedMessage })
      : error);

  return (
    <div className="flex flex-col gap-2">
      <span className="text-text-muted text-[12px] font-semibold uppercase tracking-[0.08em]">
        {label}
        {required && <span aria-hidden="true">*</span>}
      </span>
      <div className={stacked ? 'flex flex-col gap-3' : 'flex items-center gap-4'}>
        <div
          className={`border-border-strong bg-surface-page flex items-center justify-center overflow-hidden rounded-card border ${previewClassName}`}
        >
          {upload.isPending ? (
            // Percentage alongside the spinner: a bare spinner does not say how
            // far along a large file is, which is what let a Save land
            // mid-transfer.
            <div className="flex flex-col items-center gap-1">
              <LoadingSpinner size="sm" />
              {typeof upload.progress === 'number' && (
                <span className="text-text-muted text-[10px]">{upload.progress}%</span>
              )}
            </div>
          ) : hasPreview && isPdfPreview ? (
            <FileText aria-label="PDF file" className="text-text-muted" size={28} />
          ) : hasPreview ? (
            <img src={previewUrl ?? undefined} alt="" className="h-full w-full object-cover" />
          ) : (
            <ImagePlus aria-hidden="true" className="text-text-muted" size={28} />
          )}
        </div>
        <div className="flex flex-col items-start gap-1">
          {/* The stacked layout is a narrow edit panel, where the roomy `outline` button
              wraps its own label; `outline-md` is the profile cards' compact one. */}
          <Button
            type="button"
            variant={stacked ? 'outline-md' : 'outline'}
            disabled={upload.isPending}
            onClick={() => inputRef.current?.click()}
          >
            {hasPreview ? replaceCaption : uploadCaption}
          </Button>
          <span className="text-text-muted text-xs">{hint}</span>
        </div>
        <input
          ref={inputRef}
          type="file"
          accept={acceptedTypes.join(',')}
          onChange={handleChange}
          className="hidden"
          aria-hidden="true"
          tabIndex={-1}
        />
      </div>
      {errorMessage && (
        <p role="alert" className="text-danger text-xs">
          {errorMessage}
        </p>
      )}
      {crop.cropDialog}
    </div>
  );
}
